// frontend/src/pages/Blog.js
// Blog index — lists all published posts, chronological. Public route.

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  Link as MuiLink,
  useTheme,
} from '@mui/material';
import { getAllPostsSorted } from '../blog/posts';

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function Blog() {
  const theme = useTheme();
  const posts = getAllPostsSorted();

  return (
    <>
      <Helmet>
        <title>Blog — Blackmount</title>
        <meta name="description" content="The Blackmount blog: browser session management, cross-device research workflows, Pocket alternatives, and productivity tools." />
        <link rel="canonical" href="https://app.blackmount.ai/blog" />
      </Helmet>

      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h1" fontWeight={700} sx={{ mb: 2 }}>
            Blog
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 6, fontWeight: 400 }}>
            Notes on browser session management, cross-device research, and productivity tools.
          </Typography>

          <Stack spacing={3}>
            {posts.map(post => (
              <Card
                key={post.slug}
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    boxShadow: '0 4px 16px rgba(37,99,235,0.08)',
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <MuiLink
                    component={RouterLink}
                    to={`/blog/${post.slug}`}
                    underline="none"
                    color="text.primary"
                  >
                    <Typography variant="h5" component="h2" fontWeight={600} sx={{ mb: 1.5 }}>
                      {post.title}
                    </Typography>
                  </MuiLink>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.65 }}>
                    {post.description}
                  </Typography>
                  <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(post.publishedAt)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">·</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {post.readingTime} read
                    </Typography>
                    {post.tags?.slice(0, 3).map(tag => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.7rem' }}
                      />
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Container>
      </Box>
    </>
  );
}
